// sample the outer border to learn the paper color (ignores already transparent pixels)
const sampleBorder = (data, width, height) => {
  const step = Math.max(1, Math.floor(Math.min(width, height) / 50));
  const samples = [];
  let transparent = 0;
  let total = 0;

  const read = (x, y) => {
    const i = (y * width + x) * 4;
    total += 1;
    if (data[i + 3] < 250) {
      transparent += 1;
      return;
    }
    samples.push([data[i], data[i + 1], data[i + 2]]);
  };

  for (let x = 0; x < width; x += step) {
    read(x, 0);
    read(x, height - 1);
  }
  for (let y = 0; y < height; y += step) {
    read(0, y);
    read(width - 1, y);
  }

  return { samples, transparentRatio: total ? transparent / total : 0 };
};

// median per channel is robust against ink touching the border
const median = (values) => {
  const sorted = [...values].sort((a, b) => a - b);
  return sorted[Math.floor(sorted.length / 2)] || 0;
};

/**
 * Removes a plain (paper-like) background from an image, e.g. a scanned signature.
 * Uses a soft edge so strokes stay smooth instead of jagged.
 *
 * @param {string} imageSrc - data URL or image URL
 * @param {number} threshold - color distance treated as background (higher removes more)
 * @returns {Promise<string>} PNG data URL with a transparent background
 */
export const removeBackground = (imageSrc, threshold = 50) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    // kung dataURL (base64), wag lagyan ng query string
    img.src = imageSrc.startsWith("data:")
      ? imageSrc
      : imageSrc + "?t=" + new Date().getTime();

    img.onload = () => {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      canvas.width = img.width;
      canvas.height = img.height;

      ctx.drawImage(img, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;

      const { samples, transparentRatio } = sampleBorder(
        data,
        canvas.width,
        canvas.height
      );

      // already transparent (or nothing to learn from): keep the image as is
      if (transparentRatio > 0.5 || samples.length === 0) {
        resolve(canvas.toDataURL("image/png"));
        return;
      }

      const r0 = median(samples.map((s) => s[0]));
      const g0 = median(samples.map((s) => s[1]));
      const b0 = median(samples.map((s) => s[2]));

      // soft ramp: below `inner` fully clear, above `outer` fully kept
      const inner = threshold * 0.6;
      const outer = threshold * 1.4;

      for (let i = 0; i < data.length; i += 4) {
        const dist = Math.sqrt(
          (data[i] - r0) ** 2 + (data[i + 1] - g0) ** 2 + (data[i + 2] - b0) ** 2
        );

        if (dist <= inner) {
          data[i + 3] = 0;
        } else if (dist < outer) {
          const keep = (dist - inner) / (outer - inner);
          data[i + 3] = Math.round(data[i + 3] * keep);
        }
      }

      ctx.putImageData(imageData, 0, 0);
      resolve(canvas.toDataURL("image/png"));
    };

    img.onerror = (err) => {
      console.error("Image load failed:", err);
      reject(err);
    };
  });
};
